import React, { useState, useEffect } from 'react';
import { extractErrorMessage } from '../lib/errors';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Check, Mail, AlertCircle, Sparkles, ExternalLink, Shield, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import { useAuth } from '../context/AuthContext';

export default function Settings() {
  const { user, refresh } = useAuth();
  const [outlookStatus, setOutlookStatus] = useState({ configured: false, connected: false, email: null });
  const [googleStatus, setGoogleStatus] = useState({ configured: false, connected: false, email: null, send_provider: null });
  const [name, setName] = useState(user?.name || '');
  const [warmup, setWarmup] = useState(null);
  const [override, setOverride] = useState('');
  const [savingSafety, setSavingSafety] = useState(false);

  const loadWarmup = async () => {
    try { const r = await api.get('/users/me/warmup'); setWarmup(r.data); }
    catch (err) { console.warn('Warmup load failed:', err); }
  };

  useEffect(() => {
    api.get('/outlook/status').then(r => setOutlookStatus(r.data)).catch(()=>{});
    api.get('/google/status').then(r => setGoogleStatus(r.data)).catch(()=>{});
    loadWarmup();
    const params = new URLSearchParams(window.location.search);
    const outlookParam = params.get('outlook');
    if (outlookParam === 'connected') { toast.success('Outlook connected'); refresh(); }
    else if (outlookParam === 'error') { toast.error('Outlook connection failed: ' + (params.get('msg') || 'unknown error')); }
    const gmailParam = params.get('gmail');
    if (gmailParam === 'connected') { toast.success('Gmail connected'); refresh(); }
    if (outlookParam || gmailParam) window.history.replaceState({}, '', window.location.pathname);
  }, [refresh]);

  useEffect(() => { if (user?.daily_send_cap_override) setOverride(String(user.daily_send_cap_override)); }, [user?.daily_send_cap_override]);

  const connectOutlook = async () => {
    try {
      const r = await api.get('/outlook/authorize');
      window.location.href = r.data.auth_url;
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Outlook not yet configured. Set MS_CLIENT_ID/SECRET/REDIRECT_URI on backend.'));
    }
  };
  const disconnectOutlook = async () => {
    await api.post('/outlook/disconnect');
    await refresh();
    const s = await api.get('/outlook/status'); setOutlookStatus(s.data);
    const g = await api.get('/google/status'); setGoogleStatus(g.data);
    toast.success('Outlook disconnected');
  };
  const connectGmail = async () => {
    try {
      const r = await api.get('/auth/google/start', { params: { intent: 'connect', next: '/app/settings' } });
      window.location.href = r.data.auth_url;
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Gmail OAuth not configured. Set GOOGLE_CLIENT_ID/SECRET/REDIRECT_URI on backend.'));
    }
  };
  const disconnectGmail = async () => {
    await api.post('/gmail/disconnect');
    await refresh();
    const g = await api.get('/google/status'); setGoogleStatus(g.data);
    toast.success('Gmail disconnected');
  };
  const setSendProvider = async (provider) => {
    try {
      const r = await api.post('/users/me/send-provider', { provider });
      setGoogleStatus(s => ({ ...s, send_provider: r.data.send_provider }));
      toast.success(`Sending via ${r.data.send_provider || 'no provider'}`);
    } catch (err) { toast.error(extractErrorMessage(err, 'Could not switch provider')); }
  };

  const saveSafety = async ({ warmup_enabled, daily_send_cap_override } = {}) => {
    setSavingSafety(true);
    try {
      const patch = {};
      if (warmup_enabled !== undefined) patch.warmup_enabled = warmup_enabled;
      if (daily_send_cap_override !== undefined) patch.daily_send_cap_override = daily_send_cap_override;
      await api.patch('/users/me', patch);
      await refresh();
      await loadWarmup();
      toast.success('Sending safety updated');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Update failed'));
    } finally {
      setSavingSafety(false);
    }
  };

  const isPro = user?.plan === 'pro';

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <h1 className="font-display text-3xl">Settings</h1>
      <p className="text-zinc-500 mt-1">Your account, Outlook connection, and sending safety.</p>

      <Card className="mt-6 bg-[#111110] border-zinc-900 p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-display text-xl flex items-center gap-2">
              <Sparkles size={18} className="text-[#d4a94a]"/>Plan
            </div>
            <div className="text-sm text-zinc-400 mt-1">
              {isPro ? (
                <>You’re on <span className="text-[#f5d67a]">Reachly Pro</span>{user?.promo_code_applied ? ` — unlocked with promo code ${user.promo_code_applied}.` : '.'}
                {user?.plan_expires_at ? ` Renews / expires ${new Date(user.plan_expires_at).toLocaleDateString()}.` : ' Free-forever access.'}
                </>
              ) : 'You’re on the Free plan.'}
            </div>
          </div>
          {!isPro && <Button className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium" asChild><a href="/checkout">Upgrade</a></Button>}
        </div>
      </Card>

      <Card className="mt-4 bg-[#111110] border-zinc-900 p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="font-display text-xl flex items-center gap-2"><Mail size={18} className="text-[#d4a94a]"/>Outlook connection</div>
            <div className="text-sm text-zinc-500 mt-1">Send campaigns from your own Outlook mailbox via Microsoft OAuth.</div>
            {!outlookStatus.configured && (
              <div className="mt-3 rounded-md border border-amber-900 bg-amber-950/40 p-3 text-xs text-amber-200 flex gap-2">
                <AlertCircle size={14} className="mt-0.5 shrink-0"/>
                <div>
                  <div className="font-medium">Admin setup required</div>
                  <div className="mt-1 text-amber-200/80">Register an Azure app at <a className="underline" target="_blank" rel="noreferrer" href="https://portal.azure.com">portal.azure.com</a>. Redirect URI: <code className="bg-black/40 px-1 rounded">{`${window.location.origin.replace(/\/$/, '')}/api/outlook/callback`}</code>. Required permissions: <code>Mail.Send</code>, <code>Mail.Read</code>, <code>offline_access</code>. Add MS_CLIENT_ID / MS_CLIENT_SECRET / MS_REDIRECT_URI to /app/backend/.env and restart backend.</div>
                </div>
              </div>
            )}
          </div>
          {outlookStatus.connected ? (
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-2 text-emerald-400 text-sm"><Check size={16}/>Connected</div>
              <button onClick={disconnectOutlook} className="text-xs text-red-400 hover:text-red-300">Disconnect</button>
            </div>
          ) : (
            <Button disabled={!outlookStatus.configured} className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium disabled:opacity-50" onClick={connectOutlook}>
              Connect Outlook <ExternalLink size={14} className="ml-1"/>
            </Button>
          )}
        </div>
        {outlookStatus.connected && outlookStatus.email && (
          <div className="mt-4 pt-4 border-t border-zinc-900 text-sm text-zinc-400">
            <span className="text-zinc-500">Signed in as</span> <span className="text-zinc-200">{outlookStatus.email}</span>
          </div>
        )}
      </Card>

      {/* Gmail connection — mirror of the Outlook card. Both can be connected
          simultaneously; the picker below decides which one campaigns send through. */}
      <Card className="mt-4 bg-[#111110] border-zinc-900 p-6" data-testid="gmail-connection-card">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="font-display text-xl flex items-center gap-2"><Mail size={18} className="text-[#d4a94a]"/>Gmail connection</div>
            <div className="text-sm text-zinc-500 mt-1">Send campaigns from your own Gmail mailbox via Google OAuth.</div>
            {!googleStatus.configured && (
              <div className="mt-3 rounded-md border border-amber-900 bg-amber-950/40 p-3 text-xs text-amber-200 flex gap-2">
                <AlertCircle size={14} className="mt-0.5 shrink-0"/>
                <div>
                  <div className="font-medium">Admin setup required</div>
                  <div className="mt-1 text-amber-200/80">Add GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET / GOOGLE_REDIRECT_URI to backend .env and restart.</div>
                </div>
              </div>
            )}
          </div>
          {googleStatus.connected ? (
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-2 text-emerald-400 text-sm"><Check size={16}/>Connected</div>
              <button onClick={disconnectGmail} className="text-xs text-red-400 hover:text-red-300" data-testid="gmail-disconnect-btn">Disconnect</button>
            </div>
          ) : (
            <Button disabled={!googleStatus.configured} className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium disabled:opacity-50" onClick={connectGmail} data-testid="gmail-connect-btn">
              Connect Gmail <ExternalLink size={14} className="ml-1"/>
            </Button>
          )}
        </div>
        {googleStatus.connected && googleStatus.email && (
          <div className="mt-4 pt-4 border-t border-zinc-900 text-sm text-zinc-400">
            <span className="text-zinc-500">Signed in as</span> <span className="text-zinc-200">{googleStatus.email}</span>
          </div>
        )}
      </Card>

      {/* Sending provider picker — only shows when BOTH providers are connected.
          Otherwise there's nothing to pick. */}
      {outlookStatus.connected && googleStatus.connected && (
        <Card className="mt-4 bg-[#111110] border-zinc-900 p-6" data-testid="sending-provider-card">
          <div className="font-display text-xl flex items-center gap-2"><Shield size={18} className="text-[#d4a94a]"/>Active sending provider</div>
          <div className="text-sm text-zinc-500 mt-1">Campaigns send from this account. You can switch anytime — new sends use the selected provider.</div>
          <div className="mt-4 flex flex-wrap gap-2">
            {['outlook', 'gmail', 'auto'].map(p => {
              const active = (googleStatus.send_provider === p) || (p === 'auto' && !user?.send_provider_override);
              return (
                <button key={p} onClick={()=>setSendProvider(p)} data-testid={`provider-${p}-btn`}
                  className={`px-4 py-2 rounded-md border text-sm transition-colors ${active ? 'bg-[#1a1815] text-[#f5d67a] border-[#3a2f18]' : 'bg-transparent text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-zinc-200'}`}>
                  {p === 'auto' ? 'Auto (prefer Outlook)' : p.charAt(0).toUpperCase() + p.slice(1)}
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {/* SENDING SAFETY (warmup + daily cap) */}
      <Card className="mt-4 bg-[#111110] border-zinc-900 p-6" data-testid="sending-safety-card">
        <div className="flex items-start justify-between gap-6">
          <div className="flex-1">
            <div className="font-display text-xl flex items-center gap-2"><Shield size={18} className="text-[#d4a94a]"/>Sending safety</div>
            <div className="text-sm text-zinc-500 mt-1">
              Reachly caps your daily volume to protect deliverability. Brand-new inboxes ramp up over ~2 weeks so Microsoft doesn't flag you as spam.
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Today</div>
            <div className="text-3xl font-display text-[#f5d67a]" data-testid="safety-usage">
              {warmup ? `${warmup.sent_today}/${warmup.effective_cap}` : '—'}
            </div>
            <div className="text-[10px] text-zinc-500">sends used</div>
          </div>
        </div>

        <div className="mt-5 grid md:grid-cols-3 gap-3">
          <div className="rounded-md border border-zinc-800 bg-[#0a0a0b] p-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Plan cap</div>
            <div className="text-lg text-zinc-100 mt-1">{warmup ? `${warmup.plan_cap}/day` : '—'}</div>
            <div className="text-[10px] text-zinc-500 mt-0.5">{warmup?.plan === 'pro' ? 'Reachly Pro' : 'Free plan'}</div>
          </div>
          <div className="rounded-md border border-zinc-800 bg-[#0a0a0b] p-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Warmup</div>
            <div className="text-lg text-zinc-100 mt-1 flex items-center gap-2">
              {warmup?.warmup_enabled ? (warmup.warmup_day === null ? 'ready' : `day ${warmup.warmup_day}`) : 'off'}
              <Switch
                checked={!!warmup?.warmup_enabled}
                onCheckedChange={(v)=>saveSafety({ warmup_enabled: !!v })}
                data-testid="warmup-toggle"
                className="data-[state=checked]:bg-[#d4a94a] ml-1"
              />
            </div>
            <div className="text-[10px] text-zinc-500 mt-0.5">
              {warmup?.warmup_enabled
                ? (warmup.ramp_cap < warmup.plan_cap ? `Ramp cap ${warmup.ramp_cap}/day today` : 'Fully ramped')
                : 'No ramp — sending at full plan cap'}
            </div>
          </div>
          <div className="rounded-md border border-zinc-800 bg-[#0a0a0b] p-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Timezone</div>
            <div className="text-lg text-zinc-100 mt-1 truncate">{user?.timezone || 'UTC'}</div>
            <div className="text-[10px] text-zinc-500 mt-0.5">auto-detected from your browser</div>
          </div>
        </div>

        <div className="mt-5 rounded-md border border-zinc-800 bg-[#0a0a0b] p-3">
          <Label className="text-xs text-zinc-500">Personal override <span className="text-zinc-600">(optional, must be ≤ plan cap of {warmup?.plan_cap ?? '—'})</span></Label>
          <div className="mt-2 flex items-center gap-2">
            <Clock size={14} className="text-zinc-500"/>
            <Input
              type="number"
              min={1}
              max={warmup?.plan_cap || 200}
              placeholder="Use plan default"
              value={override}
              onChange={e => setOverride(e.target.value)}
              className="w-32 h-9 bg-[#111110] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a]"
              data-testid="safety-override-input"
            />
            <Button
              size="sm"
              disabled={savingSafety}
              className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium"
              onClick={()=>saveSafety({ daily_send_cap_override: parseInt(override) || 0 })}
              data-testid="safety-override-save"
            >Save</Button>
            {user?.daily_send_cap_override && (
              <button
                onClick={()=>{ setOverride(''); saveSafety({ daily_send_cap_override: 0 }); }}
                className="text-xs text-zinc-500 hover:text-red-400"
                data-testid="safety-override-clear"
              >Clear</button>
            )}
          </div>
          <div className="mt-2 text-[11px] text-zinc-500">
            Set a lower cap while you warm up a new mailbox, or if you noticed replies dropping. Leave blank to use the plan default.
          </div>
        </div>
      </Card>

      <Card className="mt-4 bg-[#111110] border-zinc-900 p-6">
        <div className="font-display text-xl">Profile</div>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-zinc-300">Name</Label>
            <Input value={name} onChange={e=>setName(e.target.value)} className="mt-1.5 bg-[#0a0a0b] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a]"/>
          </div>
          <div>
            <Label className="text-zinc-300">Email</Label>
            <Input defaultValue={user?.email} disabled className="mt-1.5 bg-[#0a0a0b] border-zinc-800 text-zinc-500"/>
          </div>
        </div>
      </Card>
    </div>
  );
}
